import global from "../global.js";
import {DIRECTION_NORTH_WEST, DIRECTION_SOUTH_WEST, DIRECTION_SOUTH_EAST, DIRECTION_NORTH_EAST} from "./Axis.js";
import Transform from "./Transform.js";

import Style from "../utils/Style.js";

const QUADRANT_NORTH_WEST = 0,
    QUADRANT_SOUTH_WEST = 1,
    QUADRANT_SOUTH_EAST = 2,
    QUADRANT_NORTH_EAST = 3;

const RUBIK_NORTH_WEST = 0,
    RUBIK_SOUTH_WEST = 1,
    RUBIK_SOUTH_EAST = 2,
    RUBIK_NORTH_EAST = 3;

const log = global.log.getLogger("f_mtac.scene.frame");
log.setLevel(log.levels.WARN);

/**
 * @brief Manage the drawing of the rubik, the distance and the quadrant
 * @param {*} view
 * @param {*} svg
 * @param {*} conf configuration = settings of the grid
 * @param {*} layer parent node of frame_grids, frame_rubik
 * @param {*} layer_quadrant layer for quadrant
 * @param {*} distance_quadrant layer for distance
 */
let Frame = function (view, svg, conf, layer, layer_quadrant, distance_layer) {
    const style = new Style();

    var is_extended = false; // is_extended = true for the extended grid
    Object.defineProperty(this, "is_extended", {
        set: (value) => {
            is_extended = value;
        },
        get: () => is_extended
    });

    var resizing_distance = undefined; // float for square distance during grid resize
    // resizing_distance: if != 0 contains the distance of the current desktop, while resizing
    // resizing_distance is set to 0 at the end of grid_resize (see Scene)
    Object.defineProperty(this, "resizing_distance", {
        set: (value) => {
            resizing_distance = value;
        }
    });

    // Init quadrant
    let quadrant = layer_quadrant.append("g").attr("class", "quadrant");
    var frame_quadrant_direction = Array.from(Array(4), () => quadrant.append("rect"));
    style.rule_set(
        "quadrant",
        `
        g.quadrant rect {
            width: calc(1 / 2 * var(--wb));
            height: calc(1 / 2 * var(--wb));
        }
    `
    );

    // Init rubik
    var frame_rubik = layer.append("g").attr("class", "frame_rubik");
    var frame_rubik_square = Array.from(Array(4), () => frame_rubik.append("rect"));

    // Init distance (i.e. grid scale)
    let distance = distance_layer.append("g").attr("class", "distance");
    var distance_text_group = distance.append("g").attr("id", "grid_scale_overlay");
    var distance_frame = distance_text_group.append("rect").attr("rx", global.app.scale.wb / 4);
    var distance_text = distance_text_group.append("text").attr("id", "grid_scale_text");
    var distance_square = distance_text_group.append("g");
    distance_square.append("line").attr("x1", "0").attr("y1", "20").attr("x2", "100").attr("y2", "20").attr("stroke", "white").attr("stroke-width", "2px");
    distance_square.append("line").attr("x1", "0").attr("y1", "80").attr("x2", "100").attr("y2", "80").attr("stroke", "white").attr("stroke-width", "2px");
    distance_square.append("line").attr("x1", "20").attr("y1", "0").attr("x2", "20").attr("y2", "100").attr("stroke", "white").attr("stroke-width", "2px");
    distance_square.append("line").attr("x1", "80").attr("y1", "0").attr("x2", "80").attr("y2", "100").attr("stroke", "white").attr("stroke-width", "2px");
    distance_square.append("rect").attr("id", "rubik-square-nw").attr("x", "22").attr("y", "22").attr("width", "27").attr("height", "27");
    distance_square.append("rect").attr("id", "rubik-square-sw").attr("x", "22").attr("y", "51").attr("width", "27").attr("height", "27");
    distance_square.append("rect").attr("id", "rubik-square-se").attr("x", "51").attr("y", "51").attr("width", "27").attr("height", "27");
    distance_square.append("rect").attr("id", "rubik-square-ne").attr("x", "51").attr("y", "22").attr("width", "27").attr("height", "27");

    var FQW,
        FQH, // size of the whole Frame Quadrant
        FQX,
        FQY; // top left position.x,y of the frame quadrant

    /**
     * @brief Draw the quadrants (the 4 colored squares at the corners)
     * For basic picture: top left quadrant is at position.x,y and frame quadrant size is picture size
     * For georef picture:
     *  - Now: hide the quadrants
     *  - Before: the quadrant are at the corner of the picture's bounding box (after rotation)
     * For GIS:
     *  - Now: hide the quadrants
     *  - Before: top left quadrant is at (0,0) and frame quadrant size is window size
     */
    const draw_quadrants = function () {
        if (is_extended === true) {
            $("#quadrant").hide();
            return;
        }
        // Initiates FQX, FQY, FQW, FQH, they will be used during transformations
        if (is_extended === true && global.scene.map.is_gis === false) {
            // if image with extended grid
            let img = document.getElementById("background_image");

            // get bounding box with the transformation (x,y,k)
            let bb = img.getBoundingClientRect();
            log.debug("Frame.draw quadrant pict geo", {FQX, FQY, FQW, FQH});
            // find the coordinates (x1,y1) of the topleft of the boundingbox without transformation
            let {x: x1, y: y1} = global.scene.transform_coordinates({x: bb.left, y: bb.top}, {ox: 0, oy: 0});
            FQX = x1;
            FQY = y1;

            // find the size (w,h) of the boundingbox without transformation
            let {x: x2, y: y2} = global.scene.transform_coordinates({x: bb.right, y: bb.bottom}, {ox: 0, oy: 0});
            FQW = x2 - x1;
            FQH = y2 - y1;
            log.debug("Frame.draw quadrant pict geo", {FQX, FQY, FQW, FQH});
        } else {
            FQW = conf.get("quadrant.width");
            FQH = conf.get("quadrant.height");
            FQX = conf.get("position.x");
            FQY = conf.get("position.y");
            log.debug("Frame.draw", {FQX, FQY, FQW, FQH});
        }

        // Set the attributes of the quadrants
        for (let d = QUADRANT_NORTH_WEST; d <= QUADRANT_NORTH_EAST; d++) {
            //frame_quadrant_direction[d].attr("x", 0).attr("y", 0).attr("width", XM).attr("height", YM).style("fill", conf.get("frame.color")[d]).style("stroke", conf.get("frame.color")[d]);
            frame_quadrant_direction[d]
                .attr("x", 0) // x position is handled by translate
                .attr("y", 0) // y position is handled by translate
                .style("fill", conf.get("frame.color")[d])
                .style("stroke", conf.get("frame.color")[d]);
        }
        $("#quadrant").show();
    };

    const draw_rubik = function () {
        if (is_extended === true) {
            $("#frame").hide();
            return;
        }

        const OX = conf.get("origin.x");
        const OY = conf.get("origin.y");
        const SW = conf.get("square.width");
        const SH = conf.get("square.height");
        log.debug("Frame.draw_rubik:", {OX, OY, SW, SH});

        // Compute rubik size and margin
        const LW = (6 * SW) / 15; // rubik one square width
        const LH = (6 * SH) / 15; // rubik one square height
        const LMW = SW / 15; // small margin for rubik
        const LMH = SH / 15; // small margin for rubik

        // Compute rubik position
        let LPW, LPH;
        switch (conf.get("frame.reference.position")) {
            case DIRECTION_NORTH_WEST: // standard
                LPW = OX;
                LPH = OY;
                break;
            case DIRECTION_SOUTH_WEST: // GIGN
                LPW = OX;
                LPH = OY - SH;
                break;
            case DIRECTION_SOUTH_EAST:
                LPW = OX - SW;
                LPH = OY - SH;
                break;
            case DIRECTION_NORTH_EAST:
                LPW = OX - SW;
                LPH = OY;
                break;
        }
        log.debug("Frame.draw_rubik:", {LMW, LMH, LPW, LPH});

        //  Compute rubik squares position
        const px_west = LPW + LMW;
        const px_east = LPW + SW - LMW - LW;
        const py_north = LPH + LMH;
        const py_south = LPH + SH - LMH - LH;
        log.debug("Frame.draw_rubik:", {px_west, px_east, py_north, py_south});

        // Set rubik squares attributes
        frame_rubik_square[RUBIK_NORTH_WEST].attr("x", px_west).attr("y", py_north).attr("width", LW).attr("height", LH).style("fill", conf.get("frame.color")[RUBIK_NORTH_WEST]).style("stroke", conf.get("frame.color")[RUBIK_NORTH_WEST]);
        frame_rubik_square[RUBIK_SOUTH_WEST].attr("x", px_west).attr("y", py_south).attr("width", LW).attr("height", LH).style("fill", conf.get("frame.color")[RUBIK_SOUTH_WEST]).style("stroke", conf.get("frame.color")[RUBIK_SOUTH_WEST]);
        frame_rubik_square[RUBIK_SOUTH_EAST].attr("x", px_east).attr("y", py_south).attr("width", LW).attr("height", LH).style("fill", conf.get("frame.color")[RUBIK_SOUTH_EAST]).style("stroke", conf.get("frame.color")[RUBIK_SOUTH_EAST]);
        frame_rubik_square[RUBIK_NORTH_EAST].attr("x", px_east).attr("y", py_north).attr("width", LW).attr("height", LH).style("fill", conf.get("frame.color")[RUBIK_NORTH_EAST]).style("stroke", conf.get("frame.color")[RUBIK_NORTH_EAST]);

        $("#frame").show();
    };

    var h1, w1;
    const draw_distance = function () {
        // Set attributes of the distance display
        distance_text.attr("x", 0).attr("y", 0);
        if (global.storage.selected_context.projection.project !== null) {
            display_square_distance();

            // set text position
            var {width: w, height: h, x, y} = distance_text.node().getBBox();
            log.debug("Frame.draw", {w, h, x, y});
            const sp_sq = 1.8 * h; // add space for the square
            w += sp_sq; // add space for the square
            x = -0.5 * w + sp_sq;
            y = -0.5 * h - y;
            distance_text.attr("x", x).attr("y", y);

            // set frame position and size
            h1 = 1.5 * h; // adding padding
            w1 = w + 0.5 * h1; // adding padding
            x = -0.5 * w1;
            y = -0.5 * h1;
            distance_frame.attr("x", x).attr("y", y).attr("width", w1).attr("height", h1);

            // set square position
            distance_square.attr("style", `transform: translate(${x + 0.5 * h}px, ${y + 0.05 * h}px) scale(${(1.4 * h) / 100})`);

            // set rubik colors
            $("#rubik-square-nw").attr("fill", conf.get("frame.color")[RUBIK_NORTH_WEST]);
            $("#rubik-square-sw").attr("fill", conf.get("frame.color")[RUBIK_SOUTH_WEST]);
            $("#rubik-square-se").attr("fill", conf.get("frame.color")[RUBIK_SOUTH_EAST]);
            $("#rubik-square-ne").attr("fill", conf.get("frame.color")[RUBIK_NORTH_EAST]);

            distance_text_group.attr("visibility", "visible");
        } else {
            //grid scale not visible if sitac is not georeferenced
            distance_text_group.attr("visibility", "hidden");
        }
    };

    /**
     * @brief Draw the elements of the frame
     * For basic picture, top left quadrant is at position.x,y and frame quadrant size is picture size
     * For geo picture, top left quadrant fits the picture bounding box (takes into account rotation)
     * For GIS, top left quadrant is at (0,0) and frame quadrant size is window size
     */
    this.draw = function () {
        log.debug("Frame.draw in");
        draw_quadrants();
        draw_rubik();
        draw_distance();
        this.transform.refresh();
    };

    /**
     * @brief compute translation of a given quadrant depending of the screen move or zoom
     * @param {*} d type of the quadrant, ex DIRECTION_SOUTH_EAST
     * @param {*} x horizontal move of the screen
     * @param {*} y vertical move of the screen
     * @param {*} k screen zoom factor
     * @returns translation string
     */
    let translate_quadrants = (x, y, k) => {
        const quadrant_size = 0.5 * global.app.scale.wb;
        const {top, left, bottom, right} = view.margin;
        let dx_west, dx_east, dy_north, dy_south;

        // for extended grid, no matter the position or the origin
        if (global.scene.map.is_gis === true) {
            // SITAC background is a GIS
            // top left quadrant is at (0,0) and frame quadrant size is window size
            dx_west = Math.max(x, left);
            dx_east = Math.min(x + window.innerWidth * k, window.innerWidth - right) - quadrant_size;
            dy_north = Math.max(y, top);
            dy_south = Math.min(y + window.innerHeight * k, window.innerHeight - bottom) - quadrant_size;
        } else {
            // SITAC background is a picture
            if (is_extended === true) {
                // extended grid (geolocalized picture)
                dx_west = Math.max(x + FQX * k, left);
                dy_north = Math.max(y + FQY * k, top);
                dy_south = Math.min(y + (FQY + FQH) * k, svg.height - bottom) - quadrant_size;
                dx_east = Math.min(x + (FQX + FQW) * k, svg.width - right) - quadrant_size;
            } else {
                // not extended (not geolocalized picture)
                dx_west = Math.max(x, left);
                dx_east = Math.min(x + FQW * k, svg.width - right) - quadrant_size;
                dy_north = Math.max(y, top);
                dy_south = Math.min(y + FQH * k, svg.height - bottom) - quadrant_size;
            }
        }
        frame_quadrant_direction[QUADRANT_NORTH_WEST].attr("transform", `translate(${dx_west},${dy_north})`);
        frame_quadrant_direction[QUADRANT_SOUTH_WEST].attr("transform", `translate(${dx_west},${dy_south})`);
        frame_quadrant_direction[QUADRANT_SOUTH_EAST].attr("transform", `translate(${dx_east},${dy_south})`);
        frame_quadrant_direction[QUADRANT_NORTH_EAST].attr("transform", `translate(${dx_east},${dy_north})`);
    };

    let translate_distance = function () {
        let dx = view.margin.left + global.app.scale.wb + 0.5 * w1;
        let dy = window.innerHeight - view.margin.bottom - 0.5 * h1;
        if (dx && dy) {
            distance_text_group.attr("transform", `translate(${dx}, ${dy})`);
        }
    };

    this.transform = new Transform({x: 0, y: 0, k: 1});

    /**
     * @brief Redraw when translation (x, y) or scale (k) change.
     * Translation happens also when the browser window is resized
     */
    this.transform.on_change(({x, y, k}) => {
        log.debug("Frame.transform.on_change", {x, y, k});
        if (is_extended === true) {
            // Update distance position
            translate_distance();
        } else {
            // Update quadrants position
            translate_quadrants(x, y, k);

            // Update rubik position and size
            frame_rubik.attr("transform", `translate(${x},${y}) scale(${k})`);
        }
    });

    /**
     * @brief Format the given float distance into a string
     * @param {float} d the distance
     * @returns a string with the distance and the unity
     */
    const format_distance = (d) => {
        // Round the distance to 3 figures or 2 figures under 100m to avoid showing decimeter.
        let distance = d < 100 ? parseFloat(d.toPrecision(2)) : parseFloat(d.toPrecision(3));
        if (distance < 1000) {
            return `${distance} m`;
        } else {
            return `${distance / 1000} km`;
        }
    };

    /**
     * @brief Draw the distance of a grid square
     */
    var display_square_distance = () => {
        let dist;
        if (resizing_distance === undefined) {
            // draw common grid distance
            dist = global.scene.map.grid.common_distance();
        } else {
            dist = resizing_distance;
        }
        log.debug("Frame.display_square_distance", {dist});
        if (dist !== undefined) {
            const map = new Map(Object.entries(global.scene.map.grid.existing_sub_grids));
            if (map.size > 0) {
                dist = 0.1 * dist;
            }
            distance_text.text(format_distance(dist)); // precision of 3 figures
        }
    };
    this.display_square_distance = () => display_square_distance();
};

export default Frame;
